import React, { useMemo } from "react";
import { Layout, getCompactor, Responsive, useContainerWidth } from 'react-grid-layout';
import { Widget } from 'system/widget/Widget';
import { WidgetContainer} from "./WidgetContainer";
import { useDashboard } from "routes/dashboard/Dashboard";
import { DashboardDataDefaults, DashboardWidgetSpacing } from "components/resources/dashboards/DashboardDocument";

// React-grid-layout tries to access process.env, but in our setup process.env is not defined. This is a documented workaround
(window as unknown as { process: { env: Record<string, string | undefined> } }).process = { env: {} };

interface WidgetGridProps {
  widgetList: Array<Widget>,
  isInLayoutMode: boolean,
  setIsInLayoutMode: (isInLayoutMode:boolean) => void,
  layouts: Partial<Record<string, Layout>> | undefined,
  savePageLayouts: (newLayouts:object, resetPage?:boolean) => void,
  onDeleteWidget: (widgetId: string) => void,
  onCopyWidget: (widget:Widget) => void,
  onUpdateWidget: (widgetId:string, updatedWidgetInfo:any, resetPage?:boolean) => Promise<any>,
  onUpdateWidgetData: (widgetId:string, updatedWidgetData:any, resetPage?:boolean) => Promise<any>,
  children?: React.ReactNode,
}

// 2 breakpoints for the easiest user experience
const breakpoints:{ [key: string]: number; } = { 
  lg: 1000,
  xs: 400
};

const marginBySpacing = {
  [DashboardWidgetSpacing.SPACIOUS]: 50,
  [DashboardWidgetSpacing.COMFORTABLE]: 30,
  [DashboardWidgetSpacing.COMPACT]: 15,
  [DashboardWidgetSpacing.MINIMAL]: 2,
};

export function WidgetGrid(props: WidgetGridProps): JSX.Element {
  const dashboard = useDashboard();
  const { width, containerRef, mounted } = useContainerWidth();

  const onLayoutChange = function(layout: Layout, layouts: Partial<Record<string, Layout>>) {
    // Only attempt to save the layout if we are in edit mode
    // Only users allowed to edit the page should be able to save the layout,
    // And we don't want to save auto layout resets
    if (!props.isInLayoutMode) {
      return;
    }

    if (props.savePageLayouts && layout && layouts) {
      props.savePageLayouts(layouts);
    }
  }

  // Make sure layouts only includes keys included in breakpoints
  const validLayouts = useMemo(() => {
    const vLayouts = props.layouts;
    for (const key in vLayouts) {
      if (!breakpoints[key]) {
        delete vLayouts[key];
      }
    }
    return vLayouts;
  }, [props.layouts]);

  const widgetElements = React.useMemo(() => {
    return props.widgetList.map((widget)=>{
      const uuid = widget.uuid || widget.id;
      return (
        <div key={uuid}>
          <WidgetContainer
            widget={widget}
            isInLayoutMode={props.isInLayoutMode}
            setIsInLayoutMode={props.setIsInLayoutMode}
            onDeleteWidget={props.onDeleteWidget}
            onCopyWidget={props.onCopyWidget}
            onUpdateWidget={props.onUpdateWidget}
            onUpdateWidgetData={props.onUpdateWidgetData} >
          </WidgetContainer>
        </div>
      );
    });
  }, [props, props.widgetList]);

  // Set the margin based on the widget spacing
  const widgetSpacing = dashboard?.data?.widgetSpacing || DashboardDataDefaults.WidgetSpacing;
  const widgetMargin:number = marginBySpacing[widgetSpacing] || marginBySpacing.SPACIOUS;

  return (
    <div ref = {containerRef as React.RefObject<HTMLDivElement>} style={{overflowX: 'hidden', padding:"15px", margin: "-15px"}}>
      {mounted && validLayouts && widgetElements &&
      <Responsive
        className="layout"
        breakpoints={breakpoints}
        cols={{ 
          lg: dashboard?.data?.fullColumnCount || DashboardDataDefaults.FullColumnCount,
          xs: 1
        }}
        width={width}
        layouts = {validLayouts}
        margin = {[widgetMargin, widgetMargin]}
        containerPadding = {[0,0]}
        dragConfig={{
          enabled: props.isInLayoutMode,
          cancel: ".drag-cancel",
          bounded: false
        }}
        resizeConfig={{enabled: props.isInLayoutMode}}
        compactor={{...getCompactor("vertical", false, false), preventCollision: false} as ReturnType<typeof getCompactor>}
        onLayoutChange={onLayoutChange}
        >
      {widgetElements}
      </Responsive>
    }
    </div>
  );
}